import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { getAllState, stateCityByPincode, updateQuatationById, getQuatationById } from "../../api"; // adjust if path differs
import { toast } from 'react-toastify';
import { Decrypt } from "../../services/data-encription";

// Validation schema
const trim = v => (typeof v === 'string' ? v.trim() : v);
const schema = yup.object({
	pincode: yup
		.string()
		.transform(trim)
		.required("Pincode is required")
		.matches(/^\d{6}$/, "Enter 6 digit pincode"),
	country: yup.string().transform(trim).required(),
	state: yup.string().transform(trim).required("State is required"),
	city: yup.string().transform(trim).required("City is required"),
	address: yup.string().required("Address is required").max(200, "Max 200 chars"),
	email: yup.string().transform(trim).required("Email required").email("Invalid email"),
	mobile: yup
		.string()
		.transform(trim)
		.required("Mobile required")
		.matches(/^[0-9]{10}$/, "Enter 10 digit mobile"),
	useGst: yup.boolean(),
	gst: yup.string().transform(trim).when("useGst", {
		is: true,
		then: (schema) =>
			schema
				.required("GST required")
				.matches(/^[0-9A-Z]{15}$/, "GSTIN must be 15 chars (A-Z/0-9)"),
		otherwise: (s) => s.optional(),
	}),
	gstEmail: yup.string().transform(trim).when("useGst", {
		is: true,
		then: (s) => s.required("GST Email required").email("Invalid email"),
	}),
	gstMobile: yup.string().transform(trim).when("useGst", {
		is: true,
		then: (s) => s.required("GST Mobile required").matches(/^[0-9]{10}$/, "Enter 10 digits"),
	}),
	companyName: yup.string().transform(trim).when("useGst", {
		is: true,
		then: (s) => s.required("Company name required"),
	}),
	companyAddress: yup.string().transform(trim).when("useGst", {
		is: true,
		then: (s) => s.optional(), // Address not required for GST company as requested
	}),
});

const CustomerDetails = ({ onBack, isStepper, onFinish }) => {
	const {
		register,
		handleSubmit,
		watch,
		setValue,
		clearErrors,
		formState: { errors, isSubmitting, isSubmitSuccessful },
		getValues,
		trigger,
	} = useForm({
		resolver: yupResolver(schema),
		reValidateMode: 'onChange',
		mode: 'all',
			criteriaMode: 'all',
			shouldUnregister: false,
		defaultValues: {
			pincode: "",
			country: "India",
			state: "",
			city: "",
			address: "",
			email: "",
			mobile: "",
			useGst: false,
			gst: "",
			gstEmail: "",
			gstMobile: "",
			companyName: "",
			companyAddress: "",
		},
	});

	// Debug: remove or guard behind env flag if not needed
	useEffect(() => {
		// Log only when there are errors while visible values look filled
		if (Object.keys(errors).length) {
			// console.debug('Form errors', errors, 'Current values', getValues());
		}
	}, [errors, getValues]);

	// Ensure boolean from watch (react-hook-form returns 'on' if unchecked logic not handled)
	const params = useParams();
	const encryptedData = params.id;
	
		let decryptedData = null;
		try {
		decryptedData = Decrypt(decodeURIComponent(encryptedData));
		// If your Decrypt returns a JSON string, use JSON.parse, otherwise just use the result.
		} catch (e) {
		console.error("Failed to decrypt quotation data", e);
		}
		//   const decryptedData = JSON.parse(decodeURIComponent(encryptedData));
	console.log("Decrypted Data:", decryptedData);
	const quotationId = decryptedData?.quotation_number;

	const watchedState = watch("state");
	const watchedCity = watch("city");

	const gstEnabledRaw = watch("useGst");
	const gstEnabled = gstEnabledRaw === true || gstEnabledRaw === 'on';
		const pincode = watch("pincode");

	const [pincodeStatus, setPincodeStatus] = useState("idle"); // idle|loading|error|success
	const pincodeFetchRef = useRef(null); // debounce ref
	const mountedRef = useRef(true);

	// Track first successful submit to suppress lingering errors
	const [didSubmitOnce, setDidSubmitOnce] = useState(false);

		// Ensure country is always set & validated (covers edge cases where default might not register before validation)
		useEffect(() => {
			setValue('country', 'India', { shouldValidate: true });
		}, [setValue]);

	// Prefill values from existing quotation (proposer/contact details)
	useEffect(() => {
		let cancelled = false;
		const firstNonEmpty = (...args) => {
			for (const v of args) {
				if (v !== undefined && v !== null && String(v).trim() !== '') return v;
			}
			return '';
		};
		const upper = (s) => (typeof s === 'string' ? s.toUpperCase() : s);
		const prefill = async () => {
			if (!quotationId) return;
			try {
				const res = await getQuatationById(quotationId);
				const raw = res?.data?.result;
				const docData = raw?.dataValues?.document_data || raw?.document_data || raw?.documentData || {};
				console.log("Quotation data for prefill", docData, ">>>>",raw);
				const proposer = docData?.proposer || {};
				const dd = docData?.documentDetails || {};
				const email = firstNonEmpty(proposer.proposer_email, docData?.proposer_email, dd?.proposer_email, raw?.proposer_email);
				const mobile = firstNonEmpty(proposer.proposer_mobile, docData?.proposer_mobile, dd?.proposer_mobile, raw?.proposer_mobile);
				const address = firstNonEmpty(proposer.proposer_address, docData?.proposer_address, dd?.proposer_address, raw?.proposer_address);
				const pincodeVal = firstNonEmpty(proposer.proposer_pin_code, docData?.proposer_pin_code, dd?.proposer_pin_code, raw?.proposer_pin_code);
				const stateVal = upper(firstNonEmpty(proposer.state, docData?.state, dd?.state, raw?.state));
				const cityVal = upper(firstNonEmpty(proposer.city, docData?.city, dd?.city, raw?.city));
				// GST-related fields from documentDetails/raw
				const gstn = firstNonEmpty(dd?.customer_gstn, docData?.customer_gstn, raw?.customer_gstn);
				const gstEmail = firstNonEmpty(dd?.customer_email, docData?.customer_email, raw?.customer_email);
				const gstMobile = firstNonEmpty(dd?.customer_mobile, docData?.customer_mobile, raw?.customer_mobile);
				const companyName = firstNonEmpty(dd?.customer_company_name, docData?.customer_company_name, raw?.customer_company_name);
				const companyAddress = firstNonEmpty(dd?.customer_company_address, docData?.customer_company_address, raw?.customer_company_address);
				if (cancelled) return;
				// Set form values; validate critical ones immediately
				setValue('email', email || '', { shouldValidate: true });
				setValue('mobile', mobile || '', { shouldValidate: true });
				setValue('address', address || '', { shouldValidate: false });
				if (pincodeVal) setValue('pincode', String(pincodeVal).replace(/[^0-9]/g, '').slice(0, 6), { shouldValidate: true, shouldDirty: true });
				if (stateVal) setValue('state', stateVal, { shouldValidate: true });
				if (cityVal) setValue('city', cityVal, { shouldValidate: true });
				if (pincodeVal && (stateVal || cityVal)) setPincodeStatus('success');
				// Prefill GST fields and enable section if any GST data present
				const hasGst = !!(gstn || gstEmail || gstMobile || companyName || companyAddress);
				if (hasGst) {
					setValue('useGst', true, { shouldValidate: true, shouldDirty: false });
					if (gstn) setValue('gst', gstn, { shouldValidate: true });
					if (gstEmail) setValue('gstEmail', gstEmail, { shouldValidate: true });
					if (gstMobile) setValue('gstMobile', String(gstMobile).replace(/[^0-9]/g, '').slice(0,10), { shouldValidate: true });
					if (companyName) setValue('companyName', companyName, { shouldValidate: true });
					if (companyAddress) setValue('companyAddress', companyAddress, { shouldValidate: false });
				}
				// Trigger validation after setting values
				try { await trigger(['email','mobile','address','pincode','state','city','useGst','gst','gstEmail','gstMobile','companyName','companyAddress']); } catch {}
			} catch (err) {
				console.warn('Prefill of customer details failed', err);
			}
		};
		prefill();
		return () => { cancelled = true; };
	}, [quotationId, setValue, trigger]);

	// Resolve pincode to state & city
		useEffect(() => {
			// Debounce pincode resolution to reduce rapid API calls & race conditions
			if (pincodeFetchRef.current) clearTimeout(pincodeFetchRef.current);
			if (!pincode || pincode.length !== 6) {
				return; // Only fetch when exactly 6 digits
			}
			pincodeFetchRef.current = setTimeout(async () => {
				try {
					setPincodeStatus("loading");
					setValue("state", "", { shouldValidate: false });
					setValue("city", "", { shouldValidate: false });
					const res = await stateCityByPincode(pincode);
					const data = res?.data?.result;
					// Defensive parsing (old component used state_id/city_id sometimes)
					const resolvedState = data?.state || data?.state_name || "";
					const resolvedCity = data?.city || data?.city_name || "";
					if (resolvedState && resolvedCity) {
						if (!mountedRef.current) return;
						setValue("state", resolvedState, { shouldValidate: true, shouldDirty: true });
						setValue("city", resolvedCity, { shouldValidate: true, shouldDirty: true });
						setPincodeStatus("success");
						await trigger(["pincode","state","city","country"]);
					} else {
						if (!mountedRef.current) return;
						setPincodeStatus("error");
						await trigger(["pincode","state","city"]);
					}
				} catch (err) {
					if (!mountedRef.current) return;
					setPincodeStatus("error");
					await trigger(["pincode","state","city"]);
				}
			}, 350); // 350ms debounce
			return () => {
				if (pincodeFetchRef.current) clearTimeout(pincodeFetchRef.current);
			};
		}, [pincode, setValue, trigger]);

		const onSubmit = async (data) => {
			// Build payload matching quotation update structure
			const proposer = {
                proposerEmail: data.email?.trim(),
                proposerMobile: data.mobile?.trim(),
                address: data.address?.trim(),
                proposerPincode: data.pincode?.trim(),
                city: data.city ? data.city.toUpperCase() : "",
                state: data.state ? data.state.toUpperCase() : "",
            };

            const payload = { ...proposer };

            if (data.useGst) {
                payload.customer_gstn = data.gst?.trim();
                payload.customer_email = (data.gstEmail || data.email)?.trim();
                payload.customer_mobile = (data.gstMobile || data.mobile)?.trim();
                payload.customer_company_name = data.companyName?.trim();
                payload.customer_company_address = data.companyAddress?.trim();
            }

			// Secret key (consistent with earlier implementation)
			// payload["yek_ku76H6felco7"] = "#$x82kc6&m*E";

			try {
				console.log("Submitting quotation customer details payload", payload);
				if (quotationId) {
					// include planId if present in decryptedData
					if (decryptedData && (decryptedData.plan_id || decryptedData.planId)) {
						payload.planId = decryptedData.plan_id || decryptedData.planId;
					}
					await updateQuatationById(quotationId, payload);
				}
				setDidSubmitOnce(true);
				// Clear conditional field errors post success to avoid lingering red states
				clearErrors();
				if (onFinish) onFinish(data, payload);
			} catch (err) {
				console.error("Failed to update quotation with customer details", err);
				const msg = (err && err.response && err.response.data && (err.response.data.message || err.response.data.error)) || err.message || "Failed to update quotation";
				toast.error(msg);
			}
		};

	const fieldWrapper = {
		display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 14, flex: '1 1 240px', minWidth: 220
	};
	const rowWrap = { display: 'flex', flexWrap: 'wrap', gap: 20 };
	const labelStyle = { fontSize: 13, fontWeight: 600, color: '#122040', display: 'flex', alignItems: 'center', gap: 4 };
	const errorStyle = { color: '#c0392b', fontSize: 11, fontWeight: 500 };
	const inputBase = {
		border: '1px solid #d4dae5', borderRadius: 10, padding: '10px 14px', fontSize: 14, fontWeight: 500,
		outline: 'none', background: '#fff', width: '100%'
	};
	const checkboxStyle = { transform: 'scale(1.1)', marginRight: 8 };

		// Handlers
		const handlePincodeChange = (e) => {
			const raw = e.target.value.replace(/[^0-9]/g, "");
			setValue("pincode", raw, { shouldValidate: true, shouldDirty: true });
			if (raw.length !== 6) {
				setPincodeStatus("idle");
				setValue("state", "", { shouldValidate: true });
				setValue("city", "", { shouldValidate: true });
				trigger(["pincode","state","city"]);
			}
		};

		const handleGstChange = (e) => {
			const val = e.target.value.toUpperCase();
			setValue("gst", val, { shouldValidate: true, shouldDirty: true });
		};

		const handleGstToggle = (e) => {
			const checked = e.target.checked;
			setValue('useGst', checked, { shouldValidate: true, shouldDirty: true });
			if (!checked) {
				// Reset GST-related fields & clear their errors to prevent post-submit flashes
				['gst','gstEmail','gstMobile','companyName','companyAddress'].forEach(f => setValue(f, '', { shouldValidate: false }));
				clearErrors(['gst','gstEmail','gstMobile','companyName','companyAddress']);
			}
		};

	// After first successful submit suppress error messages that no longer apply
	useEffect(() => {
		if (isSubmitSuccessful) {
			// Force revalidation in case of subsequent edits
			trigger();
		}
	}, [isSubmitSuccessful, trigger]);

		return (
		<div className="customer-details-step" style={{ padding: 8 }}>
			<h3 style={{ margin: '0 0 12px', fontSize: 18, fontWeight: 600, color: '#00239c' }}>Proposer Details</h3>
			<form onSubmit={handleSubmit(onSubmit)} noValidate>
				<fieldset style={{ border: '1px solid #e3e9f4', borderRadius: 12, padding: 16, marginBottom: 24 }}>
					<legend style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', padding: '0 8px', color: '#415068' }}>Contact Details</legend>
					<div style={rowWrap}>
						<div style={fieldWrapper}>
							<label style={labelStyle}>Pincode <span style={{ color: '#d63031' }}>*</span></label>
								<div style={{ position: 'relative' }}>
									<input
										type="text"
										maxLength={6}
										{...register('pincode')}
										style={{ ...inputBase, paddingRight: 60 }}
										placeholder="Enter 6-digit pincode"
										onBlur={() => trigger('pincode')}
										onChange={handlePincodeChange}
										value={watch('pincode') || ''}
									/>
									<div style={{ position: 'absolute', top: 6, right: 8, display: 'flex', gap: 4 }}>
										{pincodeStatus === 'loading' && <span style={{ fontSize: 11, color: '#555' }}>⏳</span>}
										{pincodeStatus === 'success' && <span style={{ fontSize: 12, color: '#18a76d' }}>✔</span>}
										{pincodeStatus === 'error' && <span style={{ fontSize: 12, color: '#c0392b' }}>✖</span>}
									</div>
								</div>
							{pincodeStatus === 'error' && <div style={errorStyle}>Could not resolve pincode</div>}
							{errors.pincode && <div style={errorStyle}>{errors.pincode.message}</div>}
						</div>
						<div style={fieldWrapper}>
							<label style={labelStyle}>Country <span style={{ color: '#d63031' }}>*</span></label>
								<input
									type="text"
									readOnly
									{...register('country')}
									style={{ ...inputBase, background: '#f5f7fb', color: '#122040', fontWeight: 600 }}
									value={watch('country') || 'India'}
								/>
						</div>
									<div style={fieldWrapper}>
										<label style={labelStyle}>Province / State <span style={{ color: '#d63031' }}>*</span></label>
										<input
											type="text"
											{...register('state')}
											style={{ ...inputBase, background: pincodeStatus === 'success' && watchedState ? '#f1fff7' : '#fff' }}
											placeholder={pincodeStatus === 'loading' ? 'Resolving…' : 'Auto-filled'}
											readOnly
											value={watchedState || ''}
										/>
										{errors.state && <div style={errorStyle}>{errors.state.message}</div>}
									</div>
									<div style={fieldWrapper}>
										<label style={labelStyle}>City / District <span style={{ color: '#d63031' }}>*</span></label>
										<input
											type="text"
											{...register('city')}
											style={{ ...inputBase, background: pincodeStatus === 'success' && watchedCity ? '#f1fff7' : '#fff' }}
											placeholder={pincodeStatus === 'loading' ? 'Resolving…' : 'Auto-filled'}
											readOnly
											value={watchedCity || ''}
										/>
										{errors.city && <div style={errorStyle}>{errors.city.message}</div>}
									</div>
                                    <div style={{ ...fieldWrapper, flexBasis: '100%' }}>
                                        <label style={labelStyle}>Address <span style={{ color: '#d63031' }}>*</span></label>
										<textarea rows={2} {...register('address')} style={{ ...inputBase, resize: 'vertical' }} value={watch('address') || ''} onChange={(e) => {
                                                                            setValue('address', e.target.value);
                                                                            trigger('address');
                                                                        }} placeholder="Enter address" />
                                        {errors.address && <div style={errorStyle}>{errors.address.message}</div>}
                                    </div>
                                    <div style={fieldWrapper}>
                                        <label style={labelStyle}>Email <span style={{ color: '#d63031' }}>*</span></label>
										<input type="email" {...register('email')} style={inputBase}  value={watch('email') || ''} onChange={(e) => {
                                                                            setValue('email', e.target.value);
                                                                            trigger('email');
                                                                        }} placeholder="name@example.com" />
                                        {errors.email && <div style={errorStyle}>{errors.email.message}</div>}
                                    </div>
                                    <div style={fieldWrapper}>
                                        <label style={labelStyle}>Mobile Number <span style={{ color: '#d63031' }}>*</span></label>
										<input type="text" maxLength={10} {...register('mobile')}  value={watch('mobile') || ''} onChange={(e) => {
                                                                            setValue('mobile', e.target.value);
                                                                            trigger('mobile');
                                                                        }} style={inputBase} placeholder="10-digit mobile" />
                                        {errors.mobile && <div style={errorStyle}>{errors.mobile.message}</div>}
                                    </div>
					</div>
				</fieldset>

				<fieldset style={{ border: '1px solid #e3e9f4', borderRadius: 12, padding: 16, marginBottom: 24 }}>
					<legend style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', padding: '0 8px', color: '#415068' }}>GST (Optional)</legend>
					<div style={{ marginBottom: 12 }}>
						<label style={{ ...labelStyle, cursor: 'pointer', fontWeight: 500 }}>
							<input
								type="checkbox"
								{...register('useGst')}
								style={checkboxStyle}
								onChange={handleGstToggle}
								checked={gstEnabled}
							/>
							Use GSTIN for this booking (Traveller GST only)
						</label>
						{gstEnabled && (
							<div style={{ fontSize: 12, color: '#56657a', marginTop: 4 }}>
								To claim credit of GST charged, please enter your company's GST number.
							</div>
						)}
					</div>
								{gstEnabled && (
						<div style={rowWrap}>
							<div style={fieldWrapper}>
								<label style={labelStyle}>GSTIN <span style={{ color: '#d63031' }}>*</span></label>
											<input
												type="text"
												{...register('gst')}
												style={inputBase}
												placeholder="15-char GSTIN"
												onBlur={() => trigger('gst')}
												onChange={handleGstChange}
												value={watch('gst') || ''}
											/>
								{errors.gst && <div style={errorStyle}>{errors.gst.message}</div>}
							</div>
							<div style={fieldWrapper}>
								<label style={labelStyle}>Company Name <span style={{ color: '#d63031' }}>*</span></label>
								<input type="text" {...register('companyName')} style={inputBase}  value={watch('companyName') || ''} onChange={(e) => {
                                                                            setValue('companyName', e.target.value);
                                                                            trigger('companyName');
                                                                        }} placeholder="Company name" />
								{errors.companyName && <div style={errorStyle}>{errors.companyName.message}</div>}
							</div>
							<div style={{ ...fieldWrapper, flexBasis: '100%' }}>
								<label style={labelStyle}>Company Address</label>
								<textarea rows={2} {...register('companyAddress')} style={{ ...inputBase, resize: 'vertical' }} value={watch('companyAddress') || ''} onChange={(e) => {
                                                                            setValue('companyAddress', e.target.value);
                                                                            trigger('companyAddress');
                                                                        }} placeholder="Company address" />
								{errors.companyAddress && <div style={errorStyle}>{errors.companyAddress.message}</div>}
							</div>
							<div style={fieldWrapper}>
								<label style={labelStyle}>GST Email <span style={{ color: '#d63031' }}>*</span></label>
								<input type="email" {...register('gstEmail')} value={watch('gstEmail') || ''} onChange={(e) => {
                                                                            setValue('gstEmail', e.target.value);
                                                                            trigger('gstEmail');
                                                                        }}
                                                                        style={inputBase} placeholder="gst@example.com" />
								{errors.gstEmail && <div style={errorStyle}>{errors.gstEmail.message}</div>}
							</div>
							<div style={fieldWrapper}>
								<label style={labelStyle}>GST Mobile <span style={{ color: '#d63031' }}>*</span></label>
								<input type="text" maxLength={10} {...register('gstMobile')} value={watch('gstMobile') || ''} onChange={(e) => {
                                                                            setValue('gstMobile', e.target.value);
                                                                            trigger('gstMobile');
                                                                        }} style={inputBase} placeholder="10-digit mobile" />
								{errors.gstMobile && <div style={errorStyle}>{errors.gstMobile.message}</div>}
							</div>
						</div>
					)}
				</fieldset>

				<div
                    style={{
                        display: 'flex',
                        gap: 16,
                        justifyContent: 'center',
                        marginTop: 8,
                        flexWrap: 'wrap',
                    }}
                >
                    {isStepper && (
                        <button
                            type="button"
                            onClick={onBack}
                            className="view-quotation-submit-button"
                            style={{
                                background: '#6c7a89',
                                flex: 1,
                                minWidth: 120,
                                padding: '12px 0',
                                fontSize: 16,
                            }}
                        >
                            Back
                        </button>
                    )}
                    <button
                        type="submit"
                        className="view-quotation-submit-button"
                        disabled={
                            isSubmitting ||
                            pincodeStatus === 'loading' ||
                            (pincode && pincode.length === 6 && pincodeStatus !== 'success')
                        }
                        title={pincodeStatus === 'loading' ? 'Resolving pincode...' : undefined}
                        style={{
                            flex: 1,
                            minWidth: 120,
                            padding: '12px 0',
                            fontSize: 16,
                        }}
                    >
                        {isSubmitting
                            ? 'Saving...'
                            : pincodeStatus === 'loading'
                            ? 'Resolving...'
                            : isStepper
                            ? 'Next'
                            : 'Finish'}
                    </button>
                </div>
			</form>
		</div>
	);
};

export default CustomerDetails;
